import { ReactNode } from "react";
import { Box } from "@mui/material";
import { DotLegalDatePicker, DotLegalHeader, DotLegalMultiSelect, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import MentionInput from "@common/components/mentionInput/MentionInput";
import { useTranslation } from "@localization/useTranslation";
import { RiskExposureMode } from "../../riskExposure/RiskExposure.types";
import { useTreatmentDialogStyles } from "./TreatmentDialogs.styles";
import { MitigationPlanFormApi } from "./useMitigationPlanForm";

export interface MitigationPlanFormFieldsProps {
    fields: MitigationPlanFormApi;
    consequenceOptions: Array<DotLegalSelectOption>;
    probabilityOptions: Array<DotLegalSelectOption>;
    isReadOnly?: boolean;
    // The single dialog's extras: the link to the plan's board task (rendered between the deadline row and the
    // expected-risk section) and the "Current: ..." subtitle under the expected-risk header.
    afterDeadlineSlot?: ReactNode;
    expectedRiskSubtitle?: ReactNode;
}

// The form fields all three mitigation dialogs share: name, action, security measures, responsible, deadline
// and the expected-risk-after-mitigation section. The linked-controls section is not part of this block; each
// dialog renders its own with its own available-controls semantics.
function MitigationPlanFormFields(props: MitigationPlanFormFieldsProps) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const styles = useTreatmentDialogStyles();
    const { fields, isReadOnly } = props;

    return (
        <>
            <Box sx={styles.group}>
                <DotLegalSection noMargin>
                    <Box sx={styles.sectionContent}>
                        <DotLegalTextField
                            label={translateString("name")}
                            value={fields.name}
                            onChange={fields.setName}
                            errorText={fields.validation.getErrorsForDisplay("name")}
                            maxLength={2400}
                            debounce={false}
                            noMargin
                            disabled={isReadOnly}
                        />

                        <MentionInput
                            disableMentions
                            label={translateString("action")}
                            value={fields.description}
                            onChange={fields.setDescription}
                            errorText={fields.validation.getErrorsForDisplay("description")}
                            multiline
                            rows={3}
                            noMargin
                            debounce={false}
                            disabled={isReadOnly}
                        />

                        <DotLegalMultiSelect
                            label={translateString("securityMeasures")}
                            options={fields.securityMeasureOptions}
                            chosenOptions={fields.securityMeasureIds}
                            onChange={fields.setSecurityMeasureIds}
                            isLoading={fields.isLoadingSecurityMeasures}
                            noOptionsLabel={translateString("noData")}
                            noMargin
                            disabled={isReadOnly}
                        />
                    </Box>
                </DotLegalSection>
            </Box>

            <Box sx={styles.group}>
                <DotLegalSection>
                    <Box sx={styles.twoColumnRow}>
                        <Box sx={styles.column}>
                            <DotLegalSelect
                                label={translateString("responsible")}
                                options={fields.responsibleOptions}
                                selectedItem={fields.responsibleId}
                                onChange={fields.setResponsibleId}
                                isLoading={fields.isLoadingResponsibles}
                                noOptionsLabel={translateString("noData")}
                                noMargin
                                disabled={isReadOnly}
                            />
                        </Box>
                        <Box sx={styles.column}>
                            <DotLegalDatePicker
                                label={translateString("deadline")}
                                value={fields.deadline}
                                onChange={fields.setDeadline}
                                language={getDateTimePickerLanguage()}
                                noMargin
                                disabled={isReadOnly}
                            />
                        </Box>
                    </Box>
                </DotLegalSection>
            </Box>

            {props.afterDeadlineSlot}

            <Box sx={styles.group}>
                <DotLegalSection>
                    <DotLegalHeader marginBottom={props.expectedRiskSubtitle ? 0 : 1} headerStyle={"small"} color={"primary"}>
                        {translateString("expectedRiskAfterMitigation")}
                    </DotLegalHeader>

                    {props.expectedRiskSubtitle}

                    <Box sx={styles.twoColumnRow}>
                        <Box sx={styles.column}>
                            <DotLegalSelect
                                label={translateString("consequenceAfterMitigation")}
                                options={props.consequenceOptions}
                                selectedItem={fields.expectedConsequence}
                                onChange={fields.setExpectedConsequence}
                                noOptionsLabel={translateString("noData")}
                                errorText={fields.validation.getErrorsForDisplay("expectedConsequence")}
                                noMargin
                                disabled={isReadOnly}
                            />
                        </Box>
                        <Box sx={styles.column}>
                            <DotLegalSelect
                                label={translateString("probabilityAfterMitigation")}
                                options={props.probabilityOptions}
                                selectedItem={fields.expectedProbability}
                                onChange={fields.setExpectedProbability}
                                noOptionsLabel={translateString("noData")}
                                errorText={fields.validation.getErrorsForDisplay("expectedProbability")}
                                noMargin
                                disabled={isReadOnly}
                            />
                        </Box>
                    </Box>

                    {fields.exposureEnabled && (
                        <Box sx={{ ...styles.twoColumnRow, mt: 2 }}>
                            <Box sx={styles.column}>
                                <DotLegalTextField
                                    label={translateString("estimatedMitigationCost", { currency: fields.exposureCurrencyCode ?? "" })}
                                    value={fields.estimatedCost}
                                    onChange={fields.setEstimatedCost}
                                    onlyNumber="decimal"
                                    minNumberValue={0}
                                    debounce={false}
                                    noMargin
                                    disabled={isReadOnly}
                                />
                            </Box>
                            <Box sx={styles.column}>
                                {fields.exposureMode === RiskExposureMode.Financial ? (
                                    <DotLegalTextField
                                        label={translateString("exposureAfterMitigation", { currency: fields.exposureCurrencyCode ?? "" })}
                                        value={fields.expectedExposureValue}
                                        onChange={fields.setExpectedExposureValue}
                                        onlyNumber="decimal"
                                        minNumberValue={0}
                                        debounce={false}
                                        noMargin
                                        disabled={isReadOnly}
                                    />
                                ) : (
                                    <DotLegalSelect
                                        label={translateString("exposureAfterMitigationScale")}
                                        options={fields.exposureScaleOptions}
                                        selectedItem={fields.expectedExposureValue === "" ? null : fields.expectedExposureValue}
                                        onChange={(value) => fields.setExpectedExposureValue(value ?? "")}
                                        disableSearch
                                        noOptionsLabel={translateString("noData")}
                                        noMargin
                                        disabled={isReadOnly}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </DotLegalSection>
            </Box>
        </>
    );
}

export default MitigationPlanFormFields;
